<template>
    <!-- Ajout d'une carte bancaire au compte Stripe du client (Stripe Elements).
         Utilisé dans la modale du panneau d'encaissement, la page Paiement des clients et l'empreinte du planning. -->
    <div class="ck-addcard">
        <div class="ck-addcard-head">
            <div>
                <p class="ck-addcard-eyebrow">Moyen de paiement</p>
                <h2 class="ck-addcard-title">Ajouter une carte bancaire</h2>
                <p v-if="userName" class="ck-addcard-subtitle">Elle sera enregistrée sur le compte de {{ userName }}.</p>
            </div>
            <button type="button" class="ck-addcard-close" aria-label="Fermer" :disabled="loading" @click="close()">×</button>
        </div>

        <div class="ck-addcard-body">
            <label class="ck-addcard-label" :for="'ck-addcard-name-' + _uid">Nom du titulaire</label>
            <input
                :id="'ck-addcard-name-' + _uid"
                type="text"
                class="ck-addcard-input"
                v-model="cardholderName"
                maxlength="30"
                autocomplete="cc-name"
                placeholder="Tel qu'inscrit sur la carte"
            />

            <label class="ck-addcard-label">Carte</label>
            <div class="ck-addcard-card" :class="{ 'is-focus': focused, 'is-error': !!cardError }">
                <div ref="cardElement"></div>
                <span v-if="stripLoading" class="ck-addcard-loading">Chargement de Stripe…</span>
            </div>
            <p v-if="cardError" class="ck-addcard-note is-error">{{ cardError }}</p>
            <p v-if="resultContainer" class="ck-addcard-note" :class="{ 'is-success': success, 'is-error': !success }">{{ resultContainer }}</p>
            <p class="ck-addcard-hint">Les données de la carte sont saisies chez Stripe : elles ne transitent pas par le back-office.</p>
        </div>

        <div class="ck-addcard-foot">
            <button type="button" class="ck-addcard-cancel" :disabled="loading" @click="close()">Fermer</button>
            <button type="button" class="ck-addcard-submit" :disabled="stripLoading || loading || !complete" @click="handlePaymentMethod">
                {{ loading ? 'Enregistrement…' : 'Ajouter la carte' }}
            </button>
        </div>
    </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js/pure'

export default {
    props: ['userId', 'userName'],
    name: 'AddNewPaymentCard',
    data() {
        return {
            config: {},
            stripe: null,
            cardElement: null,
            cardholderName: null,
            resultContainer: null,
            success: false,
            cardError: null,
            loading: false,
            stripLoading: true,
            complete: false,
            focused: false,
        }
    },
    methods: {
        async init() {
            this.stripe = await loadStripe(this.config.api_publishable_key)
            const elements = this.stripe.elements({ locale: 'fr' })
            this.cardElement = elements.create('card', {
                hidePostalCode: true,
                style: {
                    base: { color: '#171b23', fontFamily: 'Nunito, system-ui, sans-serif', fontSize: '15px', '::placeholder': { color: '#9aa3b1' } },
                    invalid: { color: '#c2410c' },
                },
            })
            // Montage sur une ref (et non un id) : plusieurs formulaires peuvent coexister sur une page
            this.cardElement.mount(this.$refs.cardElement)
            this.cardElement.on('ready', () => {
                this.stripLoading = false
            })
            this.cardElement.on('change', event => {
                this.complete = event.complete
                this.cardError = event.error ? event.error.message : null
            })
            this.cardElement.on('focus', () => { this.focused = true })
            this.cardElement.on('blur', () => { this.focused = false })
        },
        async addClientCard(token) {
            this.loading = true
            await axios
                .post(`/back-office/customer/${this.userId}/payment/updatePaymentMethod`, { token: token }, { withCredentials: true })
                .then(() => {
                    this.success = true
                    this.resultContainer = 'La carte a été ajoutée.'
                    this.cardholderName = ''
                    this.cardElement && this.cardElement.clear()
                    this.$emit('add-new-card')
                })
                .catch(error => {
                    this.success = false
                    this.resultContainer = error?.response?.data?.message || 'La carte n’a pas pu être enregistrée.'
                })
                .finally(() => {
                    this.loading = false
                })
        },
        async handlePaymentMethod() {
            this.resultContainer = null
            this.loading = true

            const { paymentMethod, error } = await this.stripe.createPaymentMethod({
                type: 'card',
                card: this.cardElement,
                billing_details: { name: this.cardholderName },
            })

            if (error) {
                this.success = false
                this.resultContainer = error.message
                this.loading = false
            } else {
                this.addClientCard(paymentMethod.id)
            }
        },
        close() {
            this.cardholderName = ''
            this.resultContainer = null
            this.cardElement && this.cardElement.clear()
            this.$emit('close-modal')
        },
    },
    async mounted() {
        try {
            const response = await axios.get('/api/config')
            this.config = response.data
            await this.init()
        } catch (error) {
            this.stripLoading = false
            this.success = false
            this.resultContainer = 'Stripe n’a pas pu être chargé.'
        }
    },
    beforeDestroy() {
        this.cardElement && this.cardElement.destroy()
    },
}
</script>

<style>
.ck-addcard {
    background: #fff;
    border-radius: 10px;
    color: #171b23;
    font-family: "Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif;
    max-width: 100%;
    width: 460px;
}

.ck-addcard-head {
    align-items: flex-start;
    border-bottom: 1px solid #eef0f3;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 18px 20px 14px;
}

.ck-addcard-eyebrow {
    color: #6f7785;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .1em;
    margin: 0 0 2px;
    text-transform: uppercase;
}

.ck-addcard-title {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

.ck-addcard-subtitle {
    color: #6f7785;
    font-size: 13px;
    margin: 4px 0 0;
}

button.ck-addcard-close {
    background: none;
    border: 1px solid #e2e6ec;
    border-radius: 10px;
    color: #6f7785;
    font-size: 20px;
    height: 34px;
    line-height: 1;
    width: 34px;
}

.ck-addcard-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px 20px;
}

.ck-addcard-label {
    color: #4a5261;
    font-size: 12px;
    font-weight: 600;
    margin: 6px 0 0;
}

input.ck-addcard-input,
.ck-addcard-card {
    background: #f5f6f8;
    border: 1px solid transparent;
    border-radius: 10px;
    font-size: 15px;
    min-height: 44px;
    padding: 12px 14px;
    position: relative;
    width: 100%;
}

input.ck-addcard-input:focus,
.ck-addcard-card.is-focus {
    background: #fff;
    border-color: #ffcd58;
    box-shadow: 0 0 0 3px rgba(255, 205, 88, .25);
    outline: 0;
}

.ck-addcard-card.is-error {
    border-color: #f3b8a5;
}

.ck-addcard-loading {
    color: #9aa3b1;
    font-size: 13px;
    left: 14px;
    position: absolute;
    top: 12px;
}

.ck-addcard-note {
    border-radius: 8px;
    font-size: 13px;
    margin: 4px 0 0;
}

.ck-addcard-note.is-error {
    color: #c2410c;
}

.ck-addcard-note.is-success {
    color: #1f7a55;
}

.ck-addcard-hint {
    color: #9aa3b1;
    font-size: 12px;
    margin: 6px 0 0;
}

.ck-addcard-foot {
    border-top: 1px solid #eef0f3;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding: 14px 20px 18px;
}

button.ck-addcard-cancel,
button.ck-addcard-submit {
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    min-height: 38px;
    padding: 8px 16px;
}

button.ck-addcard-cancel {
    background: #fff;
    border: 1px solid #e2e6ec;
    color: #171b23;
}

button.ck-addcard-submit {
    background: #ffcd58;
    border: 1px solid #ffcd58;
    color: #171b23;
}

button.ck-addcard-submit:disabled {
    opacity: .5;
}
</style>
